import type { WorldBuilding, WorldFloor } from "@uzverse/contracts";

/**
 * Company branding and building style as the client reads them. The data model is owned by the
 * world service (`branding`, floor `theme`, building `style`); until it is in the contracts every
 * field here is read defensively from untyped objects and falls back to a neutral default, so a
 * scene without any of it draws exactly as before.
 */

export type FloorTheme =
  "lobby" | "software" | "studio" | "marketing" | "accounting" | "leadership" | "generic";

export const FLOOR_THEMES: readonly FloorTheme[] = [
  "lobby",
  "software",
  "studio",
  "marketing",
  "accounting",
  "leadership",
  "generic",
];

export type BuildingPreset = "plaza" | "office" | "campus" | "tower" | "showroom";
export type EntranceKind = "revolving" | "sliding" | "double";

export interface Branding {
  name: string;
  logo: { light: string | null; dark: string | null; mark: string | null; icon: string | null };
  colors: { primary: string; onPrimary: string; accent: string };
}

export interface BuildingStyle {
  preset: BuildingPreset;
  entrance: EntranceKind;
  garage: boolean;
  /** Number of flag poles (0 = none). */
  flags: number;
  canopy: boolean;
  rooftopSign: boolean;
  facade: string;
}

export const DEFAULT_BRANDING: Branding = {
  name: "",
  logo: { light: null, dark: null, mark: null, icon: null },
  colors: { primary: "#5e6ad2", onPrimary: "#ffffff", accent: "#edae49" },
};

/** The style of a plain `office` building (what every building drew before styles existed). */
export const DEFAULT_STYLE: BuildingStyle = {
  preset: "office",
  entrance: "double",
  garage: false,
  flags: 0,
  canopy: true,
  rooftopSign: false,
  facade: "default",
};

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null ? (v as Record<string, unknown>) : {};
const str = (v: unknown, d: string): string => (typeof v === "string" && v.length > 0 ? v : d);
const url = (v: unknown): string | null => (typeof v === "string" && v.length > 0 ? v : null);
const bool = (v: unknown, d: boolean): boolean => (typeof v === "boolean" ? v : d);
const hex = (v: unknown, d: string): string =>
  typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v) ? v : d;
const pick = <T extends string>(v: unknown, all: readonly T[], d: T): T =>
  all.includes(v as T) ? (v as T) : d;

export function parseBranding(raw: unknown, fallbackName = ""): Branding {
  const b = obj(raw);
  const logo = obj(b["logo"]);
  const colors = obj(b["colors"]);
  return {
    name: str(b["name"], fallbackName),
    logo: {
      light: url(logo["light"]),
      dark: url(logo["dark"]),
      mark: url(logo["mark"]),
      icon: url(logo["icon"]),
    },
    colors: {
      primary: hex(colors["primary"], DEFAULT_BRANDING.colors.primary),
      onPrimary: hex(colors["onPrimary"], DEFAULT_BRANDING.colors.onPrimary),
      accent: hex(colors["accent"], DEFAULT_BRANDING.colors.accent),
    },
  };
}

const flagCount = (v: unknown): number =>
  typeof v === "number" ? Math.max(0, Math.min(6, Math.floor(v))) : v === true ? 2 : 0;

export function parseStyle(raw: unknown): BuildingStyle {
  const s = obj(raw);
  const preset = pick(s["preset"], ["plaza", "office", "campus", "tower", "showroom"] as const, "office");
  const plaza = preset === "plaza" || preset === "tower" || preset === "showroom";
  return {
    preset,
    entrance: pick(
      s["entrance"],
      ["revolving", "sliding", "double"] as const,
      plaza ? "revolving" : "double",
    ),
    garage: bool(s["garage"], false),
    flags: flagCount(s["flags"]),
    canopy: bool(s["canopy"], true),
    rooftopSign: bool(s["rooftopSign"], false),
    facade: str(s["facade"], "default"),
  };
}

/** Branding and style of a building node from its generator params (the scene facade puts them there). */
export function brandOf(
  params: Record<string, unknown>,
  fallbackName = "",
): { branding: Branding; style: BuildingStyle; styled: boolean } {
  const styled = params["style"] !== undefined || params["branding"] !== undefined;
  return {
    branding: parseBranding(params["branding"], fallbackName),
    style: parseStyle(params["style"]),
    styled,
  };
}

/** Does this building draw with a style beyond the plain office box? */
export const isStyled = (params: Record<string, unknown>): boolean =>
  params["style"] !== undefined || params["branding"] !== undefined;

const NAME_THEMES: [RegExp, FloorTheme][] = [
  [/lobi|lobby|resepsiyon|reception|zemin|ground/i, "lobby"],
  [/yaz[iı]l[iı]m|software|engineering|m[uü]hendis|dev/i, "software"],
  [/st[uü]dyo|studio|medya|media|production/i, "studio"],
  [/pazarlama|marketing|marka|brand|sat[iı]ş|sales/i, "marketing"],
  [/muhasebe|accounting|finans|finance/i, "accounting"],
  [/y[oö]netim|leadership|management|executive|y[oö]netici/i, "leadership"],
];

/** The theme of a scene floor: its `theme`, else guessed from its name, else null. */
export function themeOf(floor: Pick<WorldFloor, "name"> | null | undefined): FloorTheme | null {
  if (!floor) return null;
  const given = (floor as { theme?: unknown }).theme;
  if (typeof given === "string" && FLOOR_THEMES.includes(given as FloorTheme)) {
    return given as FloorTheme;
  }
  for (const [re, theme] of NAME_THEMES) if (re.test(floor.name)) return theme;
  return null;
}

/** Branding of the org-route building (extra fields the contract may add). */
export function brandingOfBuilding(b: WorldBuilding | null): Branding | null {
  if (!b) return null;
  const raw = (b as unknown as { branding?: unknown }).branding;
  return raw === undefined ? null : parseBranding(raw, b.name);
}
